import React from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { bindActionCreators, compose } from 'redux'
import { connect } from 'react-redux'
import { Helmet } from 'react-helmet'
import { Form, Row, Col, Card, Button, Input, Upload, Select, Switch } from 'antd'
import moment from 'moment-timezone'
import { withTranslation } from 'react-i18next'

import { Banner, QuillEditor, AspectRatioImage } from '@client/components/index'
import { AppContainer } from '@client/containers/index'
import actions from '@client/actions'
import ROLE from '@server/constants/roles'
import styles from './MoreStory.styl'
import moreStoryImage from './images/more-story.jpg'
import { IMAGE_RATIOS, DREAM_FIGHTER_TASK_CATEGORIES } from '@client/constants/index'

const { Item } = Form
const { Option } = Select

const mapStateToProps = (state) => ({
  auth: state.auth.data,
  isLogin: state.auth.isLogin,
  draftList: state.draftList.data,
  storyList: state.storyList.data,
})

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(actions, dispatch),
})

class MoreStory extends React.Component {
  static propTypes = {
    storyList: PropTypes.array.isRequired,
    actions: PropTypes.object.isRequired,
    auth: PropTypes.object.isRequired,
    draftList: PropTypes.array.isRequired,
    form: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
  }

  state = {
    isEditing: false,
    quillContent: '',
    previewImage: null,
    editStoryId: '',
    draftSelectValue: {
      key: '',
      label: '',
    },
  }

  componentDidMount() {
    const {
      auth: { id },
      actions: { getStoryList, getDraftList },
    } = this.props
    getStoryList()
    if (id) {
      getDraftList(id)
    }
  }

  componentDidUpdate(prevProps) {
    const {
      auth: { role, id },
      actions: { getDraftList },
    } = this.props
    if (role !== prevProps.auth.role && role === ROLE.ADMIN) {
      getDraftList(id)
    }
  }

  handleDraftSelectChange = (value) => {
    this.setState({
      draftSelectValue: value,
    })
  }

  deleteStory = (id) => () => {
    this.props.actions.deleteStory(id).then(() => {
      this.props.actions.getStoryList()
    })
  }

  addDraft = () => {
    this.props.actions
      .addDraft(this.props.auth.id, {
        content: this.state.quillContent,
        category: 'story',
      })
      .then(this.props.actions.getDraftList(this.props.auth.id))
  }

  loadDraft = () => {
    const targetDraft = this.props.draftList.find((draft) => {
      return draft.id === this.state.draftSelectValue.key
    })

    this.setState({
      quillContent: targetDraft.content,
    })
  }

  deleteDraft = () => {
    this.props.actions
      .deleteDraft(this.props.auth.id, this.state.draftSelectValue.key)
      .then(() => this.props.actions.getDraftList(this.props.auth.id))

    this.setState({
      draftSelectValue: {
        key: '',
        label: '',
      },
    })
  }

  beforeUpload = (file, fileList) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      this.setState({
        previewImage: {
          file,
          url: e.target.result,
        },
      })
    }
    reader.readAsDataURL(file)
  }

  addStory = () => {
    this.props.form.validateFieldsAndScroll({ scroll: { offsetTop: '100' } }, (err, formData) => {
      if (err || !this.state.previewImage) return

      const fileList = [{ name: this.state.previewImage.file.name, category: 'story' }]

      this.props.actions
        .getSignedUrl(fileList)
        .then((res) => {
          const signedUrlList = res.payload.data
          return this.props.actions.uploadToS3({
            file: this.state.previewImage.file,
            ...signedUrlList[0],
          })
        })
        .then(({ payload }) => {
          const {
            data: { url },
          } = payload
          const data = {
            cover: url,
            ...formData,
          }

          delete data.upload
          this.props.form.resetFields()
          this.setState({ previewImage: null })
          return this.props.actions.addStory(data)
        })
        .then((res) => {
          return this.props.actions.getStoryList()
        })
    })
  }

  handleQuillChange = (content) => {
    this.props.form.setFieldsValue({ content })
  }

  startEditStory = (story) => {
    const formData = { ...story }
    formData.upload = story.cover
    this.props.form.setFieldsValue(formData)
    this.setState({
      isEditing: true,
      previewImage: {
        url: story.cover,
      },
      editStoryId: story.id,
    })
    document.getElementById('pageTitle').scrollIntoView()
  }

  cancelEditStory = () => {
    this.setState({
      isEditing: false,
      previewImage: null,
      editStoryId: '',
    })
    this.props.form.resetFields()
  }

  uploadImage = () => {
    const { previewImage } = this.state
    if (previewImage.file) {
      const fileList = [{ name: this.state.previewImage.file.name, category: 'story' }]
      return this.props.actions.getSignedUrl(fileList).then((res) => {
        const signedUrlList = res.payload.data
        return this.props.actions
          .uploadToS3({
            file: this.state.previewImage.file,
            ...signedUrlList[0],
          })
          .then(({ payload: { data } }) => {
            return Promise.resolve(data.url)
          })
      })
    }
    return Promise.resolve()
  }

  updateStory = () => {
    this.props.form.validateFieldsAndScroll({ scroll: { offsetTop: '100' } }, (err, formData) => {
      if (err || !this.state.previewImage) return

      this.uploadImage()
        .then((url) => {
          const data = {
            ...formData,
          }
          if (url) data.cover = url

          delete data.upload

          this.props.form.resetFields()
          this.setState({ previewImage: null })
          return this.props.actions.updateStory(this.state.editStoryId, data)
        })
        .then((res) => {
          return this.props.actions.getStoryList()
        })
        .finally(() => {
          this.setState({ isEditing: false, editStoryId: '' })
        })
    })
  }

  render() {
    const {
      form: { getFieldDecorator },
      storyList,
      auth,
      t,
    } = this.props

    const { previewImage, draftSelectValue } = this.state
    const isAdmin = auth.role === ROLE.ADMIN
    const nonEmptyRule = [
      {
        required: true,
        message: t('必填'),
      },
    ]

    return (
      <AppContainer style={{ paddingTop: 0 }}>
        <Helmet>
          <title>{t('浪人故事')}</title>
        </Helmet>
        <Banner backgroundImage={moreStoryImage} logoFlex="center" />

        {isAdmin && (
          <Form>
            <Row id="pageTitle" type="flex" justify="center">
              <Col span={16}>
                {previewImage && (
                  <AspectRatioImage src={previewImage.url} ratio={IMAGE_RATIOS.STORY} />
                )}
              </Col>

              <Col span={16}>
                <Item multiple={false} label={t('封面')}>
                  {getFieldDecorator('upload', {
                    rules: nonEmptyRule,
                  })(
                    <Upload
                      accept="image/*"
                      showUploadList={false}
                      beforeUpload={this.beforeUpload}
                    >
                      <Button className="primary" icon="upload">
                        {t('上傳')}
                      </Button>
                    </Upload>,
                  )}
                </Item>
              </Col>

              <Col span={16}>
                <Item label={t('標題')}>
                  {getFieldDecorator('title', {
                    rules: nonEmptyRule,
                  })(<Input />)}
                </Item>
              </Col>

              <Col span={16}>
                <Item label={t('文章類別')}>
                  {getFieldDecorator('type', {
                    rules: nonEmptyRule,
                  })(
                    <Select placeholder={t('文章類別')}>
                      {['最新消息', '成果', '投票'].map((category) => {
                        return (
                          <Option key={category} value={category}>
                            {t(category)}
                          </Option>
                        )
                      })}
                    </Select>,
                  )}
                </Item>
              </Col>

              <Col span={16}>
                <Item label={t('任務類別')}>
                  {getFieldDecorator('category')(
                    <Select placeholder={t('任務類別')}>
                      <Option value="">無</Option>
                      {DREAM_FIGHTER_TASK_CATEGORIES.map((category) => {
                        return (
                          <Option key={category} value={category}>
                            {t(category)}
                          </Option>
                        )
                      })}
                    </Select>,
                  )}
                </Item>
              </Col>
              <Col span={16}>
                <Item label={t('描述')}>
                  {getFieldDecorator('description', {
                    rules: nonEmptyRule,
                  })(<Input.TextArea rows={4} />)}
                </Item>
              </Col>

              <Col span={16}>
                <Item label={t('故事內容')}>
                  {getFieldDecorator('content', {
                    valuePropName: 'content',
                    initialValue: this.state.quillContent,
                    rules: nonEmptyRule,
                  })(
                    <QuillEditor
                      actions={{ getSignedUrl: this.props.actions.getSignedUrl }}
                      onChange={this.handleQuillChange}
                    />,
                  )}
                </Item>
              </Col>
              <Col span={16}>
                <Item label={t('是否發佈')}>
                  {getFieldDecorator('is_publish', {
                    initialValue: true,
                    valuePropName: 'checked',
                  })(<Switch />)}
                </Item>
              </Col>
            </Row>

            <Row type="flex" justify="center" gutter={12}>
              <Col>
                <Button icon="save" className="info" onClick={this.addDraft}>
                  {t('儲存草稿')}
                </Button>
              </Col>
              <Col>
                {this.state.isEditing ? (
                  <>
                    <Button className="info" icon="upload" onClick={this.updateStory}>
                      {t('更新')}
                    </Button>
                    &nbsp;
                    <Button className="default" icon="rollback" onClick={this.cancelEditStory}>
                      {t('取消')}
                    </Button>
                  </>
                ) : (
                  <Button icon="plus" className="primary" onClick={this.addStory}>
                    {t('新增故事')}
                  </Button>
                )}
              </Col>
            </Row>

            <Row type="flex" justify="center" style={{ margin: 15 }}>
              <Col span={16}>
                <Select
                  labelInValue
                  onChange={this.handleDraftSelectChange}
                  value={draftSelectValue}
                >
                  {this.props.draftList.map((draft) => (
                    <Option key={draft.id} value={draft.id}>
                      {moment(draft.create_time).format('YYYY/MM/DD HH:mm')}
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>

            <Row type="flex" justify="center" gutter={12}>
              <Col>
                <Button
                  disabled={!draftSelectValue.key}
                  className="danger"
                  onClick={this.deleteDraft}
                >
                  {t('刪除草稿')}
                </Button>
              </Col>
              <Col>
                <Button disabled={!draftSelectValue.key} className="info" onClick={this.loadDraft}>
                  {t('讀取草稿')}
                </Button>
              </Col>
            </Row>
          </Form>
        )}

        <Row type="flex" justify="start">
          {storyList.map((story, index) => {
            return (
              <Col key={story.id} xs={24} sm={12} lg={6}>
                <Link to={`/more_story/${story.id}`}>
                  <Card
                    className={styles.cardContainer}
                    bordered={false}
                    bodyStyle={{ padding: 0 }}
                  >
                    <div
                      className={styles.image}
                      style={{ backgroundImage: `url(${story.cover})` }}
                    >
                      <div className={styles.overlay} />
                    </div>
                    <div className={styles.title}>{story.title}</div>
                    <div className={styles.description}>{story.description}</div>
                    <div className={styles.authorDataContainer}>
                      <div className={styles.author}>{story.author}</div>
                      <div className={styles.date}>
                        {moment(story.create_time).format('YYYY/MM/DD HH:mm')}
                      </div>
                    </div>
                  </Card>
                </Link>
                <Row type="flex" justify="center">
                  <Col>
                    {isAdmin && (
                      <Button className="danger" block onClick={this.deleteStory(story.id)}>
                        {t('刪除')}
                      </Button>
                    )}
                  </Col>
                  <Col>
                    {isAdmin && (
                      <Button
                        block
                        className="default"
                        icon="edit"
                        onClick={() => this.startEditStory(story)}
                      >
                        {t('編輯')}
                      </Button>
                    )}
                  </Col>
                </Row>
              </Col>
            )
          })}
        </Row>
      </AppContainer>
    )
  }
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  Form.create(),
  withTranslation(),
)(MoreStory)
